import { Action } from 'redux'
import { BlackjackBalanceUpdate } from '../../communication/BlackjackTypes'
import { AccountDetails, BonusStatus } from '../../communication/PlayerApiProtocol.generated'
import { SlotsBalanceUpdate } from '../../communication/SlotsTypes'
import { CurrencyCode } from '../../constants'
import { operatorConfig } from '../../OperatorConfig'
import {
	assign,
	AsyncStatus,
	handleAsyncError,
	handleAsyncRequest,
	ReduxAction,
} from '../ReduxUtils'
import {
	ACCOUNTS_ERROR,
	ENABLE_REFRESHING_ACCOUNTS,
	HIDE_TOOLBAR_BALANCE,
	PASS_SLOTS_PLAY_RESULTS,
	PASS_UPDATE_TOOLBAR_BALANCE,
	RECEIVE_ACCOUNTS,
	REQUEST_ACCOUNTS,
	SHOW_TOOLBAR_BALANCE,
} from './AccountActions'
import { mapNegativeBalanceToZero, Utils } from '../../Utils'
import { initialAccountState } from '../../config/AccountStateConfig'
import { AccountState, AccountBalance, CurrencyRole } from '../../state/AccountState'

export function accountReducer(state: AccountState = initialAccountState, anAction: Action ): AccountState {
	const action: ReduxAction<any> = anAction as ReduxAction<any>
	switch (action.type) {
	case ACCOUNTS_ERROR:
		return handleAsyncError(state)
	case REQUEST_ACCOUNTS:
		return handleRequestAccounts(state, action)
	case RECEIVE_ACCOUNTS:
		return handleBalances(state, action)
	case ENABLE_REFRESHING_ACCOUNTS:
		return handleEnableRefreshingAccount(state)
	case PASS_SLOTS_PLAY_RESULTS:
		return handleSlotsPlayResults(state, action)
	case PASS_UPDATE_TOOLBAR_BALANCE:
		return handleUpdateToolbarBalance(state, action)
	case HIDE_TOOLBAR_BALANCE:
		return hideToolbarBalance(state)
	case SHOW_TOOLBAR_BALANCE:
		return showToolbarBalance(state)
	default:
		return state
	}
}

const handleSlotsPlayResults = (state: AccountState, action: ReduxAction<SlotsBalanceUpdate>): AccountState => {
	const balances: AccountBalance[] = state.balances
	const slotPlayResultsPayload = action.payload.results
	const playResultTimestamp = slotPlayResultsPayload.timeStamp ? slotPlayResultsPayload.timeStamp : Date.now()
	let index: number

	if (slotPlayResultsPayload.sweeps_balance !== -1) {
		index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.SWEEPS))
		if ( state.lastUpdateTimeStamp === null || playResultTimestamp > state.lastUpdateTimeStamp ) {
			balances[index].amount = slotPlayResultsPayload.sweeps_balance / 100
			state.lastUpdateTimeStamp = playResultTimestamp
		}

	} else if (slotPlayResultsPayload.gold_balance !== -1) {
		index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.GOLD_COINS))
		if ( state.lastUpdateTimeStamp === null || playResultTimestamp > state.lastUpdateTimeStamp) {
			balances[index].amount = slotPlayResultsPayload.gold_balance
			state.lastUpdateTimeStamp = playResultTimestamp
		}

	} else if (slotPlayResultsPayload.sweepsWinnings) {
		index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.SWEEPS))
		balances[index].amount += slotPlayResultsPayload.sweepsWinnings / 100

	} else if (slotPlayResultsPayload.goldWinnings) {
		index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.GOLD_COINS))
		balances[index].amount += slotPlayResultsPayload.goldWinnings
	}

	const details: AccountDetails = {
		balance: balances[index].amount,
		currency: balances[index].currencyCode,
		role: balances[index].role,
	}
	balances[index] = toAccountBalance(details)
	return assign<AccountState>({}, state, {
		balances,
		bonuses: state.bonuses,
		status: AsyncStatus.COMPLETED,
		lastReceived: action.createdTime,
	})
}

const handleUpdateToolbarBalance = (state: AccountState, action: ReduxAction<BlackjackBalanceUpdate>): AccountState => {
	const balances: AccountBalance[] = state.balances
	let index: number

	index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.SWEEPS))
	balances[index].amount = action.payload.SC
	index = balances.findIndex(getCurrencyIndexFinder(CurrencyCode.GOLD_COINS))
	balances[index].amount = action.payload.GC

	const details: AccountDetails = {
		balance: balances[index].amount,
		currency: balances[index].currencyCode,
		role: balances[index].role,
	}
	balances[index] = toAccountBalance(details)
	return assign<AccountState>({}, state, {
		balances,
		bonuses: state.bonuses,
		status: AsyncStatus.COMPLETED,
		lastReceived: action.createdTime,
	})
}

const hideToolbarBalance = (state: AccountState): AccountState => {
	return assign<AccountState>({}, state, {
		balanceHidden: true,
	})
}

const showToolbarBalance = (state: AccountState): AccountState => {
	return assign<AccountState>({}, state, {
		balanceHidden: false,
	})
}

const getCurrencyIndexFinder = (currencyCode: CurrencyCode): (balance: AccountBalance) => boolean => {
	return (balance: AccountBalance) => balance.role === CurrencyRole.main && balance.currencyCode === currencyCode
}

const handleRequestAccounts = (state: AccountState, action: ReduxAction<any>): AccountState => {
	return {
		...handleAsyncRequest(state, action),
		refreshable: false,
	}
}

const handleEnableRefreshingAccount = (state: AccountState): AccountState => {
	return assign<AccountState>(
		{}, state, {
			refreshable: true,
		},
	)
}

const handleBalances = (state: AccountState, action: ReduxAction<BonusStatus>): AccountState => {

	const balances = action.payload.accounts
		.filter(a => operatorConfig.isCurrencyEnabled(a.currency))
		.map(a => toAccountBalance(a))

	return assign<AccountState>({}, state, {
		balances,
		bonuses : action.payload.bonuses,
		status : AsyncStatus.COMPLETED,
		lastReceived : action.createdTime,
		lastUpdateTimeStamp : Date.now(),
	})
}

export function toAccountBalance (a : AccountDetails)  : AccountBalance {
	return {
		amount : mapNegativeBalanceToZero(a.balance),
		currencyCode : a.currency as any,
		formattedBalance : Utils.formatWithSymbol(a.balance, a.currency),
		role : a.role as any,
	}
}
